import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Download, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import MultiSelect from '../ui/MultiSelect';

const BulkUploadModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  isDraft = false, // Whether this is for draft questions
  title = 'Bulk Upload Questions',
  uploadEndpoint = null // Custom endpoint for uploads
}) => {
  const { isAuthenticated, user } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [selectedTenantIds, setSelectedTenantIds] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedQuestionType, setSelectedQuestionType] = useState('');
  const fileInputRef = useRef(null);
  const errorRef = useRef(null);
  const validationErrorRef = useRef(null);

  // Real data from API
  const [tenants, setTenants] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [supportedQuestionTypes, setSupportedQuestionTypes] = useState({});
  const [questionTypesArray, setQuestionTypesArray] = useState([]);

  // Fetch data and reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm(); // Reset form state when modal opens
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch tenants, tags, and supported question types in parallel
      const promises = [
        superAdminAPI.getAllTags(),
        superAdminAPI.getSupportedQuestionTypes()
      ];

      // Only fetch tenants for non-draft uploads
      if (!isDraft) {
        promises.unshift(superAdminAPI.getAllTenants({ size: 100 }));
      }

      const responses = await Promise.all(promises);
      
      let responseIndex = 0;
      
      if (!isDraft) {
        const tenantsResponse = responses[responseIndex++];
        if (tenantsResponse.data?.content) {
          setTenants(tenantsResponse.data.content);
        }
      }

      const tagsResponse = responses[responseIndex++];
      const questionTypesResponse = responses[responseIndex++];

      if (tagsResponse.data) {
        setAvailableTags(tagsResponse.data);
      }

      if (questionTypesResponse.data) {
        setSupportedQuestionTypes(questionTypesResponse.data);
        setQuestionTypesArray(Object.keys(questionTypesResponse.data));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load required data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setValidationResult(null);
    setSelectedTenantIds([]);
    setSelectedTags([]);
    setNewTag('');
    setMessage('');
    setError('');
    setSelectedQuestionType('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xls|xlsx)$/i)) {
        setError('Please select an Excel file (.xlsx or .xls)');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setUploadResult(null);
      setValidationResult(null);
      setError('');
    }
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    if (!selectedQuestionType) {
      setError('Please select a question type');
      return;
    }

    setIsValidating(true);
    setValidationResult(null);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('questionType', selectedQuestionType);

      const endpoint = isDraft 
        ? '/api/super-admin/draft-questions/validate-csv'
        : '/api/super-admin/questions/validate-csv';

      const response = await superAdminAPI.validateCsv(formData, endpoint);
      
      if (response.data) {
        setValidationResult(response.data);
        if (response.data.errors && response.data.errors.length > 0) {
          setError('Validation found issues. Please review and fix them before uploading.');
        } else {
          setMessage('File validation successful! You can now proceed with the upload.');
        }
      }
    } catch (error) {
      console.error('Validation error:', error);
      setError(error.response?.data?.message || 'Validation failed. Please check your file format.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    if (!selectedQuestionType) {
      setError('Please select a question type');
      return;
    }

    // For non-draft uploads, validate tenant selection
    if (!isDraft && selectedTenantIds.length === 0) {
      setError('Please select at least one tenant or leave empty for cross-tenant questions');
    }

    setIsUploading(true);
    setUploadResult(null);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('questionType', selectedQuestionType);
      
      if (!isDraft && selectedTenantIds.length > 0) {
        selectedTenantIds.forEach(tenantId => {
          formData.append('tenantIds', tenantId);
        });
      }

      if (selectedTags.length > 0) {
        selectedTags.forEach(tag => {
          formData.append('tags', tag);
        });
      }

      const endpoint = uploadEndpoint || (isDraft 
        ? '/api/super-admin/draft-questions/upload-csv'
        : '/api/super-admin/questions/upload-csv');

      const response = await superAdminAPI.uploadCsv(formData, endpoint);
      
      if (response.data) {
        setUploadResult(response.data);
        setMessage(`Successfully uploaded ${response.data.successCount || 0} questions`);
        
        // Call parent onSubmit if provided
        if (onSubmit) {
          onSubmit(response.data);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !selectedTags.includes(newTag.trim())) {
      setSelectedTags([...selectedTags, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  };

  const downloadTemplate = async () => {
    if (!selectedQuestionType) {
      setError('Please select a question type first to download the appropriate template');
      return;
    }

    try {
      const endpoint = isDraft 
        ? `/api/super-admin/draft-questions/template/${selectedQuestionType}`
        : `/api/super-admin/questions/template/${selectedQuestionType}`;
        
      const response = await superAdminAPI.downloadTemplate(selectedQuestionType, endpoint);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedQuestionType.toLowerCase()}_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Template download error:', error);
      setError('Failed to download template. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Question Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Type *
                </label>
                <select
                  value={selectedQuestionType}
                  onChange={(e) => setSelectedQuestionType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Question Type</option>
                  {questionTypesArray.map(type => (
                    <option key={type} value={type}>
                      {type.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
                {selectedQuestionType && supportedQuestionTypes[selectedQuestionType] && (
                  <p className="mt-1 text-sm text-gray-600">
                    {supportedQuestionTypes[selectedQuestionType].description}
                  </p>
                )}
              </div>

              {/* Template Download */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex items-start">
                  <FileText className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-blue-900">Download Template</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Download the Excel template for {selectedQuestionType ? selectedQuestionType.replace(/_/g, ' ') : 'your selected question type'} to ensure proper formatting.
                    </p>
                    <button
                      onClick={downloadTemplate}
                      disabled={!selectedQuestionType}
                      className="mt-2 inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download Template
                    </button>
                  </div>
                </div>
              </div>

              {/* File Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Excel File *
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                  <div className="space-y-1 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                      >
                        <span>Upload a file</span>
                        <input
                          id="file-upload"
                          ref={fileInputRef}
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          accept=".xlsx,.xls"
                          onChange={handleFileSelect}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">Excel files up to 10MB</p>
                  </div>
                </div>
                {selectedFile && (
                  <div className="mt-2 text-sm text-gray-600">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>

              {/* Tenant Selection (only for non-draft uploads) */}
              {!isDraft && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tenants
                  </label>
                  <MultiSelect
                    options={tenants.map(tenant => ({
                      value: tenant.id,
                      label: `${tenant.name} (${tenant.code})`
                    }))}
                    selectedValues={selectedTenantIds}
                    onChange={setSelectedTenantIds}
                    placeholder="Select tenants (leave empty for cross-tenant questions)"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Leave empty to create cross-tenant questions available to all tenants
                  </p>
                </div>
              )}

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-blue-600 hover:text-blue-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <select
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select existing tag</option>
                    {availableTags
                      .filter(tag => !selectedTags.includes(tag))
                      .map(tag => (
                        <option key={tag} value={tag}>{tag}</option>
                      ))}
                  </select>
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Or type new tag"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={handleValidate}
                  disabled={!selectedFile || !selectedQuestionType || isValidating}
                  className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isValidating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                      Validating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 inline-block mr-2" />
                      Validate File
                    </>
                  )}
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || !selectedQuestionType || isUploading}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 inline-block mr-2" />
                      Upload Questions
                    </>
                  )}
                </button>
              </div>

              {/* Error Messages */}
              {error && (
                <div ref={errorRef} className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <AlertCircle className="w-5 h-5 text-red-400 mr-3 mt-0.5" />
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                </div>
              )}

              {/* Success Messages */}
              {message && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3 mt-0.5" />
                    <div className="text-sm text-green-700">{message}</div>
                  </div>
                </div>
              )}

              {/* Validation Results */}
              {validationResult && (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Validation Results</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Total rows:</span>
                      <span className="font-medium">{validationResult.totalRows || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Valid rows:</span>
                      <span className="font-medium text-green-600">{validationResult.validRows || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Invalid rows:</span>
                      <span className="font-medium text-red-600">{validationResult.invalidRows || 0}</span>
                    </div>
                  </div>

                  {validationResult.errors && validationResult.errors.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-red-900 mb-2">Errors Found:</h4>
                      <div ref={validationErrorRef} className="max-h-40 overflow-y-auto">
                        <ul className="text-sm text-red-700 space-y-1">
                          {validationResult.errors.map((error, index) => (
                            <li key={index} className="flex">
                              <span className="font-medium mr-2">Row {error.row}:</span>
                              <span>{error.message}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Upload Results */}
              {uploadResult && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">Upload Results</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Total processed:</span>
                      <span className="font-medium">{uploadResult.totalProcessed || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Successfully uploaded:</span>
                      <span className="font-medium text-green-600">{uploadResult.successCount || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Failed:</span>
                      <span className="font-medium text-red-600">{uploadResult.failureCount || 0}</span>
                    </div>
                  </div>

                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-red-900 mb-2">Upload Errors:</h4>
                      <div className="max-h-40 overflow-y-auto">
                        <ul className="text-sm text-red-700 space-y-1">
                          {uploadResult.errors.map((error, index) => (
                            <li key={index} className="flex">
                              <span className="font-medium mr-2">Row {error.row}:</span>
                              <span>{error.message}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Close Button */}
              <div className="flex justify-end pt-4 border-t">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
